import { useState, useEffect } from 'react';
import {
    useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import {
    Card,
    Typography,
    Divider,
} from "@mui/material";
import { Parser } from 'html-to-react';

import { useBillingHook } from '../../Hooks/Billings/billingHooks';

import { DetailsInvoice, BillDetailsInterface } from '../../Models/Billing/Billing';

import DateHelper from '../../Utilities/Helpers/DateHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import ComputationsHelper from '../../Utilities/Helpers/ComputationsHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { DetailsInvoiceField, BillingLicenseDetailsField, BillingDetailsField, BillingAddressField } from '../../Utilities/Enums/ParametersEnum';
import { BillingStatusEnum } from '../../Utilities/Enums/StatusEnums';
import { ValidityTypeEnum, ValidityType } from "../../Utilities/Enums/ValidityTypeEnums";

import AutoCompleteDropdown from '../../CustomViews/Elements/AutoCompleteDropdown';
import FormDatePicker from '../../CustomViews/Elements/FormDatePicker';
import FormTextField from '../../CustomViews/Elements/FormTextField';
import UserCapacityDropdown from '../../CustomViews/Elements/AutoComplete/UserCapacityDropdown';
import DataStorageDropdown from '../../CustomViews/Elements/AutoComplete/DataStorageDropdown';
import FormModulePicker from '../../CustomViews/Elements/FormModulePicker';
import FormValidityField from '../../CustomViews/Elements/FormValidityField';

const InvoiceDetailsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none !important
    border-radius: 0px !important;
    box-shadow: none !important;
    width: 100%;
    display: grid;
    grid-template-columns: 8fr 1fr 1fr;
    margin: 20px 0px;

    .itemHeaderName,
    .itemValue {
        font-size: 12px;
        font-weight: 500;
    }

    .alignRightText {
        text-align: end !important;
    }

    .itemHeaderName {
        font-weight: 700;
        color: #878a99;
        text-transform: uppercase;
    }

    .divider {
        grid-column: span 3;
        margin: 15px 0px;
    }

    .itemValueContainer {
        display: grid;
        grid-template-columns: auto 0.1fr 10fr;
        gap: 5px;
        align-items: center;

        .itemValueName, .valueItem, .colon {
            font-size: 12px;
            font-weight: 500;
        }

        .itemValueName {
            font-weight: 600;
            color: #878a99;

            .requiredText {
                color: var(--textDangerColor);
            }
        }

        .autoCompleteDropdownContainer,
        .formModulePicker,
        .customDatePicker,
        .formTextFieldContainer,
        .formValidityFieldContainer {
            width: 20vw;
        }

        .customDatePicker {
            .MuiStack-root {
                width: -webkit-fill-available;

                .MuiFormControl-root.MuiTextField-root,
                .MuiFormControl-root.MuiPickersTextField-root {
                    width: -webkit-fill-available !important;
                }
            }
        }
    }
`;

export default function ItemDetails(props: BillDetailsInterface) {
    // Variables
    let {
        billingid,
        viewType,
        setviewType,
        formData,
        setformData,
        previewData,
        setpreviewData,
        errorMessages,
        setErrorMessages,
        computeBillAmounts,
    } = props;
    let navigate = useNavigate();
    let requiredFields = RequiredParametersHelper.billings();

    // Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: billingid,
    });
    
    const q_queryKey = [CachingEnum.BILLINGS, queryStr]
    const { DropdownData, dropdownFetchStatus } = useBillingHook(q_queryKey, queryStr, false);

    //Variables
    let _billData = (viewType == ViewType.PREVIEW ? previewData : formData);
    let _modules: any = _billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.MODULES]?.map((item: any) => { return item.name })?.toString() ?? "--";

    // CachingEnum.LICENSE_TYPE, CachingEnum.VALIDITY_LIST
    const licenseTypeProps = {
        name: BillingLicenseDetailsField.LICENSE_TYPE,
        title: 'License Type',
        fieldValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.LICENSE_TYPE] ?? "",
        options: DropdownData[CachingEnum.LICENSE_TYPE] ?? [],
        isLoadingDropdown: dropdownFetchStatus ?? false,
        disabled: true,
        hideLabel: true,
        required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.LICENSE_TYPE)?.keyName != null,
        errorMessages: errorMessages,
        handleOnChange: (selectedValue: any) => {
            if(computeBillAmounts) {
                computeBillAmounts({
                    ...formData,
                    [DetailsInvoiceField.LICENSE_DETAILS]: {
                        ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                        [BillingLicenseDetailsField.LICENSE_TYPE]: selectedValue?.id ?? null,
                    }
                });
            }
        },
    },
        userCapacityProps = {
            name: BillingLicenseDetailsField.USER_CAPACITY,
            title: 'No. of Users',
            sideContentPosition: 'end',
            sideContent: 'Users',
            fieldValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.USER_CAPACITY] ?? "",
            type: 'number',
            required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.USER_CAPACITY)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            handleOnChange: (selectedValue: any) => {
                if(computeBillAmounts) {
                    computeBillAmounts({
                        ...formData,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.USER_CAPACITY]: selectedValue,
                        }
                    });
                }
            },
        },
        modulesProps = {
            name: BillingLicenseDetailsField.MODULES,
            title: 'Modules',
            fieldValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.MODULES] ?? [],
            required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.MODULES)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            currencyCode: formData?.[DetailsInvoiceField.CURRENCY_CODE] ?? GlobalFunctionHelper.getCurrencyCode(),
            handleOnChange: (selectedValue: any) => {
                if(computeBillAmounts) {
                    computeBillAmounts({
                        ...formData,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.MODULES]: selectedValue ?? [],
                        }
                    });
                }
            },
        },
        storageCapacityProps = {
            name: BillingLicenseDetailsField.STORAGE_CAPACITY,
            title: 'Data Storage',
            sideContentPosition: 'end',
            sideContent: 'GB',
            fieldValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.STORAGE_CAPACITY] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.STORAGE_CAPACITY)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            currencyCode: formData?.[DetailsInvoiceField.CURRENCY_CODE] ?? GlobalFunctionHelper.getCurrencyCode(),
            handleOnChange: (selectedValue: any) => {
                if(computeBillAmounts) {
                    computeBillAmounts({
                        ...formData,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.STORAGE_CAPACITY]: selectedValue?.id ?? null,
                        }
                    });
                }
            },
        },
        validityProps = {
            validityTitle: 'Validity',
            validityName: BillingLicenseDetailsField.VALIDITY,
            validityValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY] ?? "",
            validityRequired: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.VALIDITY)?.keyName != null,
            validityTypeTitle: 'Validity Type',
            validityTypeName: BillingLicenseDetailsField.VALIDITY_TYPE,
            validityTypeValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY_TYPE] ?? "",
            validityTypeRequired: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.VALIDITY_TYPE)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            disabled: false,
            validityHandleOnChange: (selectedValue: any) => {
                let fromNow = new Date();
                let _validityType = formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH;
                let _plusAmount = isNaN(parseFloat(selectedValue)) ? 0 : parseFloat(selectedValue);

                if(_validityType == ValidityTypeEnum.MONTH) {
                    fromNow.setMonth(fromNow.getMonth() + _plusAmount);
                }

                if(_validityType == ValidityTypeEnum.YEAR) {
                    fromNow.setFullYear(fromNow.getFullYear() + _plusAmount);
                }

                if(computeBillAmounts) {
                    computeBillAmounts({
                        ...formData,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.VALIDITY]: selectedValue ?? 0,
                            [BillingLicenseDetailsField.EXPIRY_DATE]: fromNow,  
                        }
                    });
                }
            },
            validityTypeHandleOnChange: (selectedValue: any) => {
                let fromNow = new Date();
                let _validityType = selectedValue?.id ?? ValidityTypeEnum.MONTH;
                let _validity: any = formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY] ?? "0";
                let _plusAmount: number = isNaN(parseFloat(_validity)) ? 0 : parseFloat(_validity);

                if(_validityType == ValidityTypeEnum.MONTH) {
                    fromNow.setMonth(fromNow.getMonth() + _plusAmount);
                }

                if(_validityType == ValidityTypeEnum.YEAR) {
                    fromNow.setFullYear(fromNow.getFullYear() + _plusAmount);
                }

                if(computeBillAmounts) {
                    computeBillAmounts({
                        ...formData,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.VALIDITY_TYPE]: selectedValue?.id ?? null,
                            [BillingLicenseDetailsField.EXPIRY_DATE]: fromNow,  
                        }
                    });
                }
            },
        },
        licenseIssueDateProps = {
            name: BillingLicenseDetailsField.ISSUE_DATE,
            title: 'License Issue Date',
            fieldValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.ISSUE_DATE] ?? null,
            required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.ISSUE_DATE)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({
                        ...prev,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.ISSUE_DATE]: selectedValue ?? null,
                        }
                    }));
                }
            },
        },
        licenseExpiryDateProps = {
            name: BillingLicenseDetailsField.EXPIRY_DATE,
            title: 'License Expiry Date',
            fieldValue: formData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.EXPIRY_DATE] ?? null,
            required: requiredFields?.find((field: any) => field.keyName == DetailsInvoiceField.LICENSE_DETAILS)?.fields?.find((f: any) => f.keyName == BillingLicenseDetailsField.EXPIRY_DATE)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: true,
            disabled: true,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({
                        ...prev,
                        [DetailsInvoiceField.LICENSE_DETAILS]: {
                            ...formData?.[DetailsInvoiceField.LICENSE_DETAILS],
                            [BillingLicenseDetailsField.EXPIRY_DATE]: selectedValue ?? null,
                        }
                    }));
                }
            },
        };

    //Event Handlers
    
    //Rendering Functions
    const renderFieldheader = (title: any = null, required: boolean = false) => {
        return <Typography className="itemValueName">{title ?? "--"} {required && <span className="requiredText">*</span>}</Typography>
    }

    return <InvoiceDetailsContainer className="invoiceDetailsContainer">
        <Typography className="itemHeaderName">Description</Typography>
        <Typography className="itemHeaderName">Qty</Typography>
        <Typography className="itemHeaderName alignRightText">Total</Typography>

        <Divider className="divider" />

        <div className="itemValueContainer">
                {renderFieldheader(licenseTypeProps?.title ?? "--", licenseTypeProps?.required ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">License Key ({_billData?.[DetailsInvoiceField.GENERATED_LICENSE_KEY] != null && _billData?.[DetailsInvoiceField.GENERATED_LICENSE_KEY] != "" ? _billData?.[DetailsInvoiceField.GENERATED_LICENSE_KEY] : "--"})</Typography>}
                {viewType != ViewType.PREVIEW && <AutoCompleteDropdown {...licenseTypeProps} />}
            
                {renderFieldheader(userCapacityProps?.title ?? "--", userCapacityProps?.required ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">{_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.USER_CAPACITY] ?? 0} Users</Typography>}
                {viewType != ViewType.PREVIEW && <FormTextField {...userCapacityProps} />}
                
                {renderFieldheader(modulesProps?.title ?? "--", modulesProps?.required ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">{_modules}</Typography>}
                {viewType != ViewType.PREVIEW && <FormModulePicker {...modulesProps} />}
            
                {renderFieldheader(storageCapacityProps?.title ?? "--", storageCapacityProps?.required ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">{_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.STORAGE_CAPACITY] ?? 0} GB</Typography>}
                {viewType != ViewType.PREVIEW && <DataStorageDropdown {...storageCapacityProps} />}
                
                {renderFieldheader(validityProps?.validityTitle ?? "--", validityProps?.validityRequired ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">{_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY] ?? 0} {(ValidityType?.[_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH]?.title ?? "Month")}(s)</Typography>}
                {viewType != ViewType.PREVIEW && <FormValidityField {...validityProps} />}
            
                {renderFieldheader(licenseIssueDateProps?.title ?? "--", licenseIssueDateProps?.required ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">{_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.ISSUE_DATE] != null && _billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.ISSUE_DATE] != "" ? DateHelper.getFormattedDate(_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.ISSUE_DATE], "dd/MM/yyyy") :  "--"}</Typography>}
                {viewType != ViewType.PREVIEW && <FormDatePicker {...licenseIssueDateProps} />}

                {renderFieldheader(licenseExpiryDateProps?.title ?? "--", licenseExpiryDateProps?.required ?? false)}
                <Typography className="colon">:</Typography>
                {viewType == ViewType.PREVIEW && <Typography className="valueItem">{_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.EXPIRY_DATE] != null && _billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.EXPIRY_DATE] != "" ? DateHelper.getFormattedDate(_billData?.[DetailsInvoiceField.LICENSE_DETAILS]?.[BillingLicenseDetailsField.EXPIRY_DATE], "dd/MM/yyyy") :  "--"}</Typography>}
                {viewType != ViewType.PREVIEW && <FormDatePicker {...licenseExpiryDateProps} />}
        </div>
        <Typography className="itemValue">1</Typography>
        <Typography className="itemValue alignRightText">{GlobalFunctionHelper.amountFormatter(_billData?.[DetailsInvoiceField.SUB_TOTAL] ?? 0, 2, true)}</Typography>

        <Divider className="divider" />
    </InvoiceDetailsContainer>
}
